import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface ABTest {
  id: string;
  creatorId: string;
  name: string;
  description?: string;
  type: 'TIER_PRICE' | 'TIER_NAME' | 'POST_TITLE' | 'CUSTOM';
  status: 'DRAFT' | 'RUNNING' | 'PAUSED' | 'COMPLETED';
  variantA: any;
  variantB: any;
  startDate?: Date;
  endDate?: Date;
  targetAudience?: string[];
  conversionMetric: string;
  minSampleSize?: number;
  confidenceLevel?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ABTestResults {
  test: ABTest;
  variantAData: {
    views: number;
    conversions: number;
    conversionRate: number;
  };
  variantBData: {
    views: number;
    conversions: number;
    conversionRate: number;
  };
  winner: 'A' | 'B' | null;
  confidence: number;
  statisticalSignificance: boolean;
  improvement: number;
  recommendedAction: string;
}

export interface CreateABTestData {
  name: string;
  description?: string;
  type: 'TIER_PRICE' | 'TIER_NAME' | 'POST_TITLE' | 'CUSTOM';
  variantA: any;
  variantB: any;
  startDate?: string;
  endDate?: string;
  targetAudience?: string[];
  conversionMetric: string;
  minSampleSize?: number;
  confidenceLevel?: number;
}

const abTestService = {
  // Create A/B test
  create: async (data: CreateABTestData): Promise<ABTest> => {
    const response = await axios.post(`${API_URL}/api/ab-tests`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.test;
  },

  // Get all A/B tests
  getAll: async (params?: {
    status?: string;
  }): Promise<ABTest[]> => {
    const response = await axios.get(`${API_URL}/api/ab-tests`, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.tests;
  },

  // Get single A/B test
  getById: async (id: string): Promise<ABTest> => {
    const response = await axios.get(`${API_URL}/api/ab-tests/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.test;
  },

  // Get test results
  getResults: async (id: string): Promise<ABTestResults> => {
    const response = await axios.get(`${API_URL}/api/ab-tests/${id}/results`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.results;
  },

  // Start A/B test
  start: async (id: string): Promise<ABTest> => {
    const response = await axios.post(
      `${API_URL}/api/ab-tests/${id}/start`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.test;
  },

  // Pause A/B test
  pause: async (id: string): Promise<ABTest> => {
    const response = await axios.post(
      `${API_URL}/api/ab-tests/${id}/pause`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.test;
  },

  // Resume A/B test
  resume: async (id: string): Promise<ABTest> => {
    const response = await axios.post(
      `${API_URL}/api/ab-tests/${id}/resume`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.test;
  },

  // Complete A/B test
  complete: async (id: string, winningVariant?: 'A' | 'B'): Promise<ABTest> => {
    const response = await axios.post(
      `${API_URL}/api/ab-tests/${id}/complete`,
      { winningVariant },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.test;
  },

  // Track view or conversion
  track: async (id: string, variant: 'A' | 'B', eventType: 'view' | 'conversion'): Promise<void> => {
    await axios.post(
      `${API_URL}/api/ab-tests/${id}/track`,
      { variant, eventType },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
  },

  // Delete A/B test
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/api/ab-tests/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },
};

export default abTestService;
